// theme/tokens.js

export const tokens = (mode) => {
  const shared = {
    houndGold: {
      100: "#f2e9d8",
      200: "#e5d3b1",
      300: "#d8be8b",
      400: "#cba864",
      500: "#be923d",
      600: "#987531",
      700: "#725825",
      800: "#4c3a18",
      900: "#261d0c",
    },

    scalpelTeal: {
      100: "#d1e6e5",
      200: "#a2cdcc",
      300: "#74b4b2",
      400: "#459b99",
      500: "#17827f",
      600: "#126866",
      700: "#0e4e4c",
      800: "#093433",
      900: "#051a19",
    },

    grey: {
      100: "#cfd3d8",
      200: "#e0e0e0",
      300: "#c2c2c2",
      400: "#a3a3a3",
      500: "#858585",
      600: "#666666",
      700: "#4d4d4d",
      800: "#333333",
      900: "#1a1a1a",
    },

    softRed: {
      100: "#fbeaea",
      200: "#f5c5c5",
      300: "#ef9f9f",
      400: "#e97a7a",
      500: "#e35454",
      600: "#b34646",
      700: "#843838",
      800: "#542929",
      900: "#251b1b",
    },

    nivoColorScheme: [
      "#e8c1a0",
      "#f47560",
      "#f1e15b",
      "#e8a838",
      "#61cdbb",
      "#97e3d5",
      "#f1c0e8",
    ],

    shape: {
      borderRadius: 12,
      radius: {
        xs: 6,
        sm: 8,
        md: 12,
        lg: 16,
        xl: 24,
        pill: 999,
      },
    },
  };

  shared.button = {
    gold: {
      bg: shared.houndGold[400],
      color: shared.grey[900],
      border: shared.houndGold[500],
      hoverBg: shared.houndGold[300],
      shadow: "rgba(190,146,61,0.22)",
    },

    teal: {
      bg: shared.scalpelTeal[600],
      color: shared.grey[100],
      border: shared.scalpelTeal[500],
      hoverBg: shared.scalpelTeal[500],
      shadow: "rgba(23,130,127,0.22)",
    },

    danger: {
      bg: shared.softRed[600],
      color: "#fff",
      border: shared.softRed[500],
      hoverBg: shared.softRed[500],
      shadow: "rgba(227,84,84,0.22)",
    },

    neutral: {
      bg: shared.grey[700],
      color: shared.grey[100],
      border: shared.grey[600],
      hoverBg: shared.grey[600],
      shadow: "rgba(0,0,0,0.18)",
    },

    ghost: {
      bg: "transparent",
      color: shared.grey[300],
      border: "rgba(255,255,255,0.12)",
      hoverBg: "rgba(255,255,255,0.06)",
      shadow: "rgba(0,0,0,0.18)",
    },
  };

  shared.chip = {
    default: {
      bg: "rgba(255,255,255,0.08)",
      color: shared.grey[200],
      border: "rgba(255,255,255,0.08)",
    },

    info: {
      bg: "rgba(23,130,127,0.18)",
      color: shared.scalpelTeal[200],
      border: "rgba(23,130,127,0.35)",
    },

    success: {
      bg: "rgba(116,180,130,0.16)",
      color: "#b8e0c1",
      border: "rgba(116,180,130,0.30)",
    },

    warning: {
      bg: "rgba(190,146,61,0.18)",
      color: shared.houndGold[200],
      border: "rgba(190,146,61,0.35)",
    },

    danger: {
      bg: "rgba(227,84,84,0.16)",
      color: shared.softRed[200],
      border: "rgba(227,84,84,0.32)",
    },

    tag: {
      bg: "rgba(23,130,127,0.12)",
      color: shared.scalpelTeal[200],
      border: "rgba(23,130,127,0.25)",
    },

    metric: {
      bg: "rgba(255,255,255,0.06)",
      color: shared.grey[200],
      border: "rgba(255,255,255,0.12)",
    },

    role: {
      bg: "rgba(23,130,127,0.12)",
      color: shared.scalpelTeal[200],
      border: "rgba(23,130,127,0.25)",
    },

    department: {
      bg: "rgba(190,146,61,0.12)",
      color: shared.houndGold[200],
      border: "rgba(190,146,61,0.25)",
    },

    industry: {
      bg: "rgba(255,255,255,0.06)",
      color: shared.grey[200],
      border: "rgba(255,255,255,0.12)",
    },

    label: {
      bg: "rgba(255,255,255,0.06)",
      color: shared.grey[200],
      border: "rgba(255,255,255,0.12)",
    },

    // Temporary aliases while older code is migrated
    teal: {
      bg: "rgba(23,130,127,0.18)",
      color: shared.scalpelTeal[200],
      border: "rgba(23,130,127,0.35)",
    },

    gold: {
      bg: "rgba(190,146,61,0.18)",
      color: shared.houndGold[200],
      border: "rgba(190,146,61,0.35)",
    },
  };

  const light = {
    background: {
      default: "#f4f5f5",
      paper: "#ffffff",
    },

    surface: {
      base: "#f4f5f5",
      card: "#ffffff",
      header: "#f7f7f7",
      footer: "#f2f2f2",
      raised: "#fafafa",
      elevated: "#ffffff",
      hover: "#f0f0f0",
      active: "#e9eeee",
      border: "rgba(0,0,0,0.08)",
    },

    text: {
      primary: shared.grey[900],
      secondary: shared.grey[600],
      muted: shared.grey[500],
    },
  };

  const dark = {
    background: {
      default: "#121212",
      paper: "#1a1a1a",
    },

    surface: {
      base: "#161616",
      card: "#100f0f",
      header: "#252525",
      footer: "#1b1b1b",
      raised: "#272727",
      elevated: "#2f2f2f",
      hover: "#343434",
      active: "#3a3a3a",
      border: "rgba(255,255,255,0.08)",
    },

    text: {
      primary: shared.grey[100],
      secondary: shared.grey[300],
      muted: shared.grey[500],
    },
  };

  return {
    ...shared,
    ...(mode === "dark" ? dark : light),
  };
};